import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getClients, deleteClient, getUsers, getReportUrl, importClientsCSV } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatApiError, getStatusBadgeClass, formatCurrency, getClientDisplayName } from '../../lib/utils';
import { CLIENT_STATUSES, KYC_STATUSES, buildUserOptions } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, MagnifyingGlass, DotsThree, Eye, Pencil, Trash, Users, DownloadSimple, UploadSimple } from '@phosphor-icons/react';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');
  const [viewFilter, setViewFilter] = useState('heads_unmapped');
  const [scopeFilter, setScopeFilter] = useState('my_customers');
  const [amFilter, setAmFilter] = useState('all');
  const [omFilter, setOmFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleteId, setDeleteId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [usersMap, setUsersMap] = useState({});
  const [usersList, setUsersList] = useState([]);
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importClientsCSV(file);
      toast.success(res.data.message);
      fetchClients();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const fetchClients = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (kycFilter !== 'all') params.kyc_status = kycFilter;
      if (viewFilter === 'heads_unmapped') params.family_filter = 'heads_unmapped';
      if (scopeFilter !== 'all') params.scope = scopeFilter;
      if (amFilter !== 'all') params.am = amFilter;
      if (omFilter !== 'all') params.om = omFilter;
      if (sortBy) params.sort_by = sortBy;
      if (sortOrder) params.sort_order = sortOrder;
      
      const [res, usersRes] = await Promise.all([getClients(params), getUsers()]);
      setClients(res.data);
      const map = {};
      usersRes.data.forEach(u => { map[u.id] = u.name; });
      setUsersMap(map);
      setUsersList(usersRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [statusFilter, kycFilter, viewFilter, scopeFilter, amFilter, omFilter, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <span className="text-zinc-300 ml-1">↕</span>;
    return <span className="text-[#002FA7] ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteClient(deleteId);
      toast.success('Client deleted successfully');
      setClients(clients.filter(c => c.id !== deleteId));
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Clients
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {clients.length} client{clients.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" size="sm" data-testid="export-clients-btn">
                <DownloadSimple size={16} className="mr-1" /> <span className="hidden sm:inline">Export</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => window.open(getReportUrl('clients', 'csv'), '_blank')}>
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(getReportUrl('clients', 'excel'), '_blank')}>
                Export as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" className="rounded-sm relative" size="sm" disabled={importing} data-testid="import-clients-csv-btn">
            <UploadSimple size={16} className="mr-1" /> <span className="hidden sm:inline">{importing ? 'Importing...' : 'Import CSV'}</span>
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVImport}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          </Button>
          <Button
            onClick={() => navigate('/clients/new')}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
            size="sm"
            data-testid="create-client-button"
          >
            <Plus size={16} className="mr-1" />
            <span className="hidden sm:inline">Add Client</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Search by name, phone, email, or PAN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-sm"
              data-testid="clients-search-input"
            />
          </div>
        </form>
        <div className="flex gap-2 overflow-x-auto pb-1 filter-bar">
        <SearchableSelect
          value={statusFilter}
          onValueChange={setStatusFilter}
          options={[{ value: 'all', label: 'All Statuses' }, ...CLIENT_STATUSES.map(s => ({ value: s, label: s }))]}
          placeholder="Status"
          triggerClassName="w-[160px]"
          data-testid="client-status-filter"
        />
        <SearchableSelect
          value={kycFilter}
          onValueChange={setKycFilter}
          options={[{ value: 'all', label: 'All KYC' }, ...KYC_STATUSES.map(s => ({ value: s, label: s }))]}
          placeholder="KYC Status"
          triggerClassName="w-[160px]"
          data-testid="kyc-status-filter"
        />
        <SearchableSelect
          value={amFilter}
          onValueChange={setAmFilter}
          options={[{ value: 'all', label: 'All AMs' }, ...buildUserOptions(usersList.filter(u => u.status === 'active' || u.id))]}
          placeholder="Account Manager"
          searchPlaceholder="Search..."
          triggerClassName="w-[170px]"
          data-testid="am-filter"
        />
        <SearchableSelect
          value={omFilter}
          onValueChange={setOmFilter}
          options={[{ value: 'all', label: 'All OMs' }, ...buildUserOptions(usersList.filter(u => u.status === 'active' || u.id))]}
          placeholder="Operations Manager"
          searchPlaceholder="Search..."
          triggerClassName="w-[180px]"
          data-testid="om-filter"
        />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 filter-bar">
        <div className="flex border border-zinc-200 rounded-sm overflow-hidden flex-shrink-0" data-testid="scope-toggle">
          <button
            onClick={() => setScopeFilter('my_customers')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${scopeFilter === 'my_customers' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
            data-testid="scope-my-btn"
          >
            My Customers
          </button>
          <button
            onClick={() => setScopeFilter('my_team')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-zinc-200 ${scopeFilter === 'my_team' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
            data-testid="scope-team-btn"
          >
            My Team
          </button>
          <button
            onClick={() => setScopeFilter('my_operations')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-zinc-200 ${scopeFilter === 'my_operations' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
            data-testid="scope-ops-btn"
          >
            My Operations
          </button>
          {isAdmin && (
            <button
              onClick={() => setScopeFilter('all')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-zinc-200 ${scopeFilter === 'all' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
              data-testid="scope-all-btn"
            >
              All
            </button>
          )}
        </div>
        <div className="flex border border-zinc-200 rounded-sm overflow-hidden flex-shrink-0" data-testid="view-toggle">
          <button
            onClick={() => setViewFilter('all')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewFilter === 'all' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
            data-testid="view-all-btn"
          >
            Everyone
          </button>
          <button
            onClick={() => setViewFilter('heads_unmapped')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-zinc-200 ${viewFilter === 'heads_unmapped' ? 'bg-[#002FA7] text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
            data-testid="view-heads-btn"
          >
            Heads & Unmapped
          </button>
        </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : clients.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500">No clients found</p>
            <Button
              onClick={() => navigate('/clients/new')}
              variant="link"
              className="text-[#002FA7] mt-2"
            >
              Add your first client
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold cursor-pointer select-none" onClick={() => handleSort('full_name')}>Name<SortIcon field="full_name" /></TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">AUM</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Account Manager</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">SIP</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold cursor-pointer select-none" onClick={() => handleSort('last_activity_date')}>Last Activity<SortIcon field="last_activity_date" /></TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Next Follow-up</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold cursor-pointer select-none" onClick={() => handleSort('last_portfolio_reviewed_on')}>Portfolio Review<SortIcon field="last_portfolio_reviewed_on" /></TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold cursor-pointer select-none" onClick={() => handleSort('created_at')}>Created<SortIcon field="created_at" /></TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => (
                <TableRow key={client.id} className="hover:bg-zinc-50" data-testid={`client-row-${client.id}`}>
                  <TableCell className="font-medium">
                    <Link to={`/clients/${client.id}`} className="hover:text-[#002FA7]">
                      {getClientDisplayName(client)}
                    </Link>
                    {client.is_family_head && (
                      <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-semibold">Family</span>
                    )}
                    {client.show_as_individual && !client.is_family_head && (
                      <span className="ml-2 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-semibold">Family Member</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {viewFilter === 'heads_unmapped' && client.is_family_head ? (
                      <span className="text-sm font-medium text-zinc-700">
                        {client.family_aum || client.aum_range || '—'}
                      </span>
                    ) : (
                      <span className="text-sm text-zinc-600">{client.aum_range || '—'}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-zinc-600">
                      {client.account_managers?.[0] ? usersMap[client.account_managers[0]] || '—' : '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {viewFilter === 'heads_unmapped' && client.is_family_head ? (
                      <span className="text-sm font-semibold text-[#002FA7] tabular-nums">
                        {client.family_total_sip ? formatCurrency(client.family_total_sip) + '/mo' : '—'}
                      </span>
                    ) : (
                      <span className="text-sm text-zinc-600 tabular-nums">
                        {client.total_sip ? formatCurrency(client.total_sip) + '/mo' : '—'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {client.last_activity_date ? (
                      <span className="text-sm text-zinc-600">
                        {formatDate(client.last_activity_date)}
                        <span className="text-xs text-zinc-400 ml-1">({client.last_activity_type})</span>
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">No activity</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {client.next_follow_up_date ? (
                      <span className="text-sm text-zinc-600">
                        {formatDate(client.next_follow_up_date)}
                        {client.next_follow_up_type && <span className="text-xs text-zinc-400 ml-1">({client.next_follow_up_type})</span>}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {client.last_portfolio_reviewed_on ? (
                      <span className="text-sm text-zinc-600">{formatDate(client.last_portfolio_reviewed_on)}</span>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-zinc-400">{client.created_at ? formatDate(client.created_at) : '—'}</span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <DotsThree size={18} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
                          <Eye size={16} className="mr-2" /> View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}/edit`)}>
                          <Pencil size={16} className="mr-2" /> Edit
                        </DropdownMenuItem>
                        {isAdmin && (
                          <DropdownMenuItem 
                            onClick={() => setDeleteId(client.id)}
                            className="text-red-600"
                          >
                            <Trash size={16} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this client? This action cannot be undone and will remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
